import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAdminSession } from "./AdminSessionContext";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: "ri-dashboard-line", end: true },
  { to: "/admin/estado", label: "Estado del sitio", icon: "ri-toggle-line" },
  { to: "/admin/settings", label: "Datos del sitio", icon: "ri-store-2-line" },
  { to: "/admin/banner", label: "Banner Principal", icon: "ri-slideshow-3-line" },
  { to: "/admin/recinto", label: "Sección «El recinto»", icon: "ri-building-line" },
  { to: "/admin/textos", label: "Textos del sitio", icon: "ri-text" },
  { to: "/admin/google-drive", label: "Google Drive", icon: "ri-drive-line" },
  { to: "/admin/bodegas", label: "Bodegas", icon: "ri-archive-line" },
  { to: "/admin/planes", label: "Planes", icon: "ri-layout-grid-line" },
  { to: "/admin/faqs", label: "Preguntas frecuentes", icon: "ri-question-line" },
  { to: "/admin/testimonios", label: "Testimonios", icon: "ri-chat-quote-line" },
  { to: "/admin/bloques", label: "Bloques de contenido", icon: "ri-stack-line" },
  { to: "/admin/paginas", label: "Páginas", icon: "ri-file-text-line" },
  { to: "/admin/contactos", label: "Contactos", icon: "ri-mail-line" },
  { to: "/admin/formulario", label: "Formulario de contacto", icon: "ri-survey-line" },
  { to: "/admin/correo", label: "Correo del formulario", icon: "ri-send-plane-line" },
  { to: "/admin/usuarios", label: "Usuarios", icon: "ri-team-line" },
  { to: "/admin/medios", label: "Medios", icon: "ri-image-line" },
];

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { admin, logout } = useAdminSession();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate("/admin/login");
  }

  return (
    <div className="min-h-screen flex bg-background-100">
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-primary-950 text-background-100">
        <div className="px-6 py-6 border-b border-primary-900">
          <p className="font-heading font-bold text-background-50">ESPACIO BORMAN</p>
          <p className="text-xs text-background-400">Panel de administración</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition ${
                  isActive ? "bg-primary-800 text-background-50" : "text-background-300 hover:bg-primary-900"
                }`
              }
            >
              <i className={`${item.icon} text-lg`} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-primary-900">
          <p className="px-3 text-xs text-background-400 truncate">{admin?.email}</p>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-2 w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-background-300 hover:bg-primary-900 transition cursor-pointer"
          >
            <i className="ri-logout-box-line text-lg" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between bg-primary-950 text-background-50 px-4 py-3">
          <p className="font-heading font-semibold text-sm">ESPACIO BORMAN — Admin</p>
          <button type="button" onClick={handleLogout} className="text-xs text-background-300">
            Salir
          </button>
        </header>
        <nav className="md:hidden flex overflow-x-auto gap-1 bg-primary-900 px-2 py-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${
                  isActive ? "bg-primary-700 text-background-50" : "text-background-300"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 p-4 md:p-8 max-w-4xl w-full mx-auto">{children}</main>
      </div>
    </div>
  );
}
